import type {
    CustomSurveySnapshot,
    SurveyOption,
    SurveyQuestion,
    SurveyQuestionConfig,
    SurveyQuestionValidation,
    SurveyRatingConfig,
    SurveySection,
} from '@/api/surveys';

export type FieldType =
    | 'text'
    | 'textarea'
    | 'select'
    | 'multiselect'
    | 'rating'
    | 'number'
    | 'email'
    | 'date'
    | 'cpf'
    | 'phone';

interface FieldTypeDefinition {
    value: FieldType;
    label: string;
    description: string;
}

interface QuestionPosition {
    sectionIndex: number;
    questionIndex: number;
}

export interface PublishIssue {
    id: string;
    message: string;
}

const EMAIL_REGEX = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';

export const DEFAULT_CUSTOM_SURVEY_NAME = 'Novo formulário';

export const FIELD_TYPES: FieldTypeDefinition[] = [
    {
        value: 'text',
        label: 'Texto curto',
        description: 'Resposta breve em uma linha.',
    },
    {
        value: 'textarea',
        label: 'Texto longo',
        description: 'Resposta aberta com mais contexto.',
    },
    {
        value: 'select',
        label: 'Escolha única',
        description: 'Uma alternativa entre opções definidas.',
    },
    {
        value: 'multiselect',
        label: 'Múltipla escolha',
        description: 'Uma ou mais alternativas.',
    },
    {
        value: 'rating',
        label: 'Escala',
        description: 'Pontuação numérica em uma escala.',
    },
    {
        value: 'number',
        label: 'Número',
        description: 'Valor numérico livre.',
    },
    {
        value: 'email',
        label: 'E-mail',
        description: 'Endereço de e-mail validado.',
    },
    {
        value: 'date',
        label: 'Data',
        description: 'Campo para data.',
    },
    {
        value: 'cpf',
        label: 'CPF',
        description: 'Documento com normalização de dígitos.',
    },
    {
        value: 'phone',
        label: 'Telefone',
        description: 'Telefone com normalização de dígitos.',
    },
];

const SUPPORTED_FIELD_TYPES = FIELD_TYPES.map((fieldType) => fieldType.value);

const OPTION_FIELD_TYPES: FieldType[] = ['select', 'multiselect'];

const PLACEHOLDER_FIELD_TYPES: FieldType[] = [
    'text',
    'textarea',
    'select',
    'number',
    'email',
    'date',
    'cpf',
    'phone',
];

export const DEFAULT_RATING_CONFIG = {
    min: 1,
    max: 5,
    step: 1,
    left_label: 'Pouco',
    right_label: 'Muito',
};

export const MAX_RATING_VALUES = 15;

const EMPTY_SECTION_ID = 'section_1';

const normalizeSurveyName = (name: string) => name.trim().toLocaleLowerCase();

export const getNextCustomSurveyName = (surveys: Array<{ name: string }>) => {
    const existingNames = new Set(surveys.map((survey) => normalizeSurveyName(survey.name)));

    if (!existingNames.has(normalizeSurveyName(DEFAULT_CUSTOM_SURVEY_NAME))) {
        return DEFAULT_CUSTOM_SURVEY_NAME;
    }

    let suffix = 2;
    while (existingNames.has(normalizeSurveyName(`${DEFAULT_CUSTOM_SURVEY_NAME} ${suffix}`))) {
        suffix += 1;
    }

    return `${DEFAULT_CUSTOM_SURVEY_NAME} ${suffix}`;
};

const createId = (prefix: string) => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return `${prefix}_${crypto.randomUUID()}`;
    }

    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export const cloneSnapshot = (snapshot: CustomSurveySnapshot): CustomSurveySnapshot =>
    JSON.parse(JSON.stringify(snapshot));

const slugify = (value: string) => {
    const normalized = value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 48);

    return normalized || 'pergunta';
};

const isSupportedFieldType = (value: string): value is FieldType =>
    SUPPORTED_FIELD_TYPES.includes(value as FieldType);

export const isOptionFieldType = (value: string): value is 'select' | 'multiselect' =>
    OPTION_FIELD_TYPES.includes(value as FieldType);

export const supportsPlaceholder = (value: string) =>
    PLACEHOLDER_FIELD_TYPES.includes(value as FieldType);

export const resolveFieldType = (question: SurveyQuestion): FieldType => {
    const fieldType = question.field_type?.toLowerCase() ?? 'text';
    return isSupportedFieldType(fieldType) ? fieldType : 'text';
};

export const validationForType = (type: FieldType): SurveyQuestionValidation => {
    if (type === 'select' || type === 'multiselect') {
        return { style: type, input_mode: 'text' };
    }

    if (type === 'rating' || type === 'number') {
        return { style: 'number', input_mode: 'numeric', normalize: ['trim'] };
    }

    if (type === 'email') {
        return {
            style: 'email',
            input_mode: 'email',
            regex: EMAIL_REGEX,
            message: 'Digite um e-mail valido',
            normalize: ['trim', 'lowercase'],
        };
    }

    if (type === 'cpf') {
        return {
            style: 'cpf',
            input_mode: 'numeric',
            mask: '000.000.000-00',
            regex: '^\\d{11}$',
            message: 'Digite um CPF valido',
            normalize: ['trim', 'digits_only'],
        };
    }

    if (type === 'phone') {
        return {
            style: 'phone',
            input_mode: 'tel',
            mask: '(00) 00000-0000',
            regex: '^\\d{10,11}$',
            message: 'Digite um telefone valido',
            normalize: ['trim', 'digits_only'],
        };
    }

    if (type === 'date') {
        return {
            style: 'date',
            input_mode: 'text',
            mask: '99/99/9999',
            message: 'Digite uma data',
            normalize: ['trim'],
        };
    }

    return { style: 'text', input_mode: 'text', normalize: ['trim'] };
};

export const defaultSnapshot = (name = DEFAULT_CUSTOM_SURVEY_NAME): CustomSurveySnapshot => ({
    name,
    instructions: '',
    sections: [
        {
            id: EMPTY_SECTION_ID,
            title: '',
            description: '',
            questions: [],
        },
    ],
});

export const createSection = (): SurveySection => ({
    id: createId('section'),
    title: '',
    description: '',
    questions: [],
});

export const getAllQuestions = (snapshot: CustomSurveySnapshot) =>
    snapshot.sections.flatMap((section) => section.questions);

export const createUniqueFieldName = (
    value: string,
    questions: SurveyQuestion[],
    excludeQuestionId?: string,
) => {
    const baseName = slugify(value);
    const usedNames = new Set(
        questions
            .filter((question) => !excludeQuestionId || question.id !== excludeQuestionId)
            .map((question) => question.field_name?.toLowerCase())
            .filter(Boolean),
    );

    if (!usedNames.has(baseName)) return baseName;

    let index = 2;
    while (usedNames.has(`${baseName}_${index}`)) {
        index += 1;
    }

    return `${baseName}_${index}`;
};

export const createQuestion = (questions: SurveyQuestion[] = []): SurveyQuestion => {
    const id = createId('field');

    return {
        id,
        field_name: createUniqueFieldName('pergunta', questions, id),
        field_label: '',
        required: false,
        field_type: 'text',
        validation: validationForType('text'),
        config: {
            placeholder: '',
            help_text: '',
        },
    };
};

export const optionLabel = (option: SurveyOption) =>
    typeof option === 'string' ? option : option.label;

const optionValue = (option: SurveyOption) => (typeof option === 'string' ? option : option.value);

export const normalizeOption = (option: SurveyOption) => ({
    label: optionLabel(option),
    value: optionValue(option) || slugify(optionLabel(option)),
});

export const createOption = (index: number) => ({
    label: `Opção ${index}`,
    value: `opcao_${index}`,
});

export const defaultOptions = () => [createOption(1), createOption(2)];

export const normalizeRatingConfig = (rating?: Partial<SurveyRatingConfig>): SurveyRatingConfig => {
    const min = Number.isFinite(rating?.min) ? Number(rating?.min) : DEFAULT_RATING_CONFIG.min;
    const step = Math.max(
        1,
        Number.isFinite(rating?.step) ? Number(rating?.step) : DEFAULT_RATING_CONFIG.step,
    );
    const minimumMax = min + step;
    const maximumMax = min + step * (MAX_RATING_VALUES - 1);
    const requestedMax = Number.isFinite(rating?.max)
        ? Number(rating?.max)
        : DEFAULT_RATING_CONFIG.max;

    return {
        ...DEFAULT_RATING_CONFIG,
        ...rating,
        min,
        max: Math.min(Math.max(requestedMax, minimumMax), maximumMax),
        step,
    };
};

export const findQuestionPosition = (
    snapshot: CustomSurveySnapshot,
    questionId: string,
): QuestionPosition | null => {
    for (let sectionIndex = 0; sectionIndex < snapshot.sections.length; sectionIndex += 1) {
        const questionIndex = snapshot.sections[sectionIndex].questions.findIndex(
            (question) => question.id === questionId,
        );

        if (questionIndex >= 0) {
            return { sectionIndex, questionIndex };
        }
    }

    return null;
};

export const getActiveSectionId = (
    snapshot: CustomSurveySnapshot,
    activeQuestionId: string | null,
) => {
    if (activeQuestionId) {
        const position = findQuestionPosition(snapshot, activeQuestionId);
        if (position) return snapshot.sections[position.sectionIndex].id;
    }

    return snapshot.sections[0]?.id ?? EMPTY_SECTION_ID;
};

export const countQuestions = (snapshot: CustomSurveySnapshot) => getAllQuestions(snapshot).length;

export const normalizeSnapshotForBuilder = (
    snapshot: CustomSurveySnapshot | undefined,
): CustomSurveySnapshot => {
    const source = snapshot ? cloneSnapshot(snapshot) : defaultSnapshot();
    const sections = source.sections.length ? source.sections : defaultSnapshot().sections;

    return {
        name: source.name ?? DEFAULT_CUSTOM_SURVEY_NAME,
        instructions: source.instructions ?? '',
        sections: sections.map((section, sectionIndex) => ({
            id: section.id || (sectionIndex === 0 ? EMPTY_SECTION_ID : createId('section')),
            title: section.title ?? '',
            description: section.description ?? '',
            questions: section.questions.map((question) => {
                const fieldType = resolveFieldType(question);
                const config = question.config ?? {};

                return {
                    ...question,
                    id: question.id || createId('field'),
                    field_label: question.field_label ?? '',
                    required: Boolean(question.required),
                    field_type: fieldType,
                    validation: question.validation ?? validationForType(fieldType),
                    config:
                        fieldType === 'rating'
                            ? {
                                  ...config,
                                  rating: normalizeRatingConfig(config.rating),
                              }
                            : config,
                };
            }),
        })),
    };
};

export const normalizeSnapshotForSave = (snapshot: CustomSurveySnapshot): CustomSurveySnapshot => {
    const next = normalizeSnapshotForBuilder(snapshot);
    const normalizedQuestions: SurveyQuestion[] = [];

    next.name = next.name.trim() || DEFAULT_CUSTOM_SURVEY_NAME;
    next.instructions = next.instructions ?? '';

    next.sections = next.sections.map((section) => ({
        ...section,
        title: section.title ?? '',
        description: section.description ?? '',
        questions: section.questions.map((question) => {
            const fieldType = resolveFieldType(question);
            const fieldNameBase = question.field_label?.trim() || question.field_name || 'pergunta';
            const config: SurveyQuestionConfig = {
                help_text: question.config?.help_text ?? '',
            };

            if (supportsPlaceholder(fieldType)) {
                config.placeholder = question.config?.placeholder ?? '';
            }

            const normalizedQuestion: SurveyQuestion = {
                ...question,
                field_name: createUniqueFieldName(fieldNameBase, normalizedQuestions, question.id),
                field_label: question.field_label ?? '',
                required: Boolean(question.required),
                field_type: fieldType,
                validation: validationForType(fieldType),
                config,
            };

            if (isOptionFieldType(fieldType)) {
                normalizedQuestion.options = (
                    question.options?.length ? question.options : defaultOptions()
                ).map(normalizeOption);

                if (fieldType === 'select') {
                    normalizedQuestion.config = {
                        ...normalizedQuestion.config,
                        allow_other: Boolean(question.config?.allow_other),
                    };
                }
            } else {
                delete normalizedQuestion.options;
            }

            if (fieldType === 'rating') {
                normalizedQuestion.config = {
                    ...normalizedQuestion.config,
                    rating: normalizeRatingConfig(question.config?.rating),
                };
            }

            normalizedQuestions.push(normalizedQuestion);
            return normalizedQuestion;
        }),
    }));

    return next;
};

export const validatePublishableSnapshot = (snapshot: CustomSurveySnapshot): PublishIssue[] => {
    const issues: PublishIssue[] = [];
    const allQuestions = getAllQuestions(snapshot);

    if (!snapshot.name.trim()) {
        issues.push({
            id: 'survey-title',
            message: 'O formulário precisa ter um título.',
        });
    }

    if (allQuestions.length === 0) {
        issues.push({
            id: 'survey-question-count',
            message: 'Adicione pelo menos uma pergunta.',
        });
    }

    snapshot.sections.forEach((section, sectionIndex) => {
        if (snapshot.sections.length > 1 && !section.title.trim()) {
            issues.push({
                id: `section-${section.id}-title`,
                message: `A seção ${sectionIndex + 1} precisa ter um título.`,
            });
        }

        section.questions.forEach((question, questionIndex) => {
            const label = question.field_label?.trim() || '';
            const questionLabel = `Pergunta ${questionIndex + 1}`;
            const fieldType = question.field_type.toLowerCase();

            if (!question.field_name.trim()) {
                issues.push({
                    id: `question-${question.id}-field-name`,
                    message: `${questionLabel} precisa de uma chave interna.`,
                });
            }

            if (!label) {
                issues.push({
                    id: `question-${question.id}-label`,
                    message: `${questionLabel} precisa de um enunciado.`,
                });
            }

            if (!isSupportedFieldType(fieldType)) {
                issues.push({
                    id: `question-${question.id}-type`,
                    message: `${label || questionLabel} tem um tipo de campo inválido.`,
                });
            }

            if (fieldType === 'select' || fieldType === 'multiselect') {
                const values = (question.options ?? []).map(optionValue).filter(Boolean);
                const uniqueValues = new Set(values);

                if (values.length < 2) {
                    issues.push({
                        id: `question-${question.id}-options-count`,
                        message: `${label || questionLabel} precisa de pelo menos duas opções.`,
                    });
                }

                if (uniqueValues.size !== values.length) {
                    issues.push({
                        id: `question-${question.id}-options-unique`,
                        message: `${label || questionLabel} tem opções duplicadas.`,
                    });
                }
            }

            if (fieldType === 'rating') {
                const rating = question.config?.rating;

                if (
                    !rating ||
                    rating.min >= rating.max ||
                    rating.step <= 0 ||
                    (rating.max - rating.min) / rating.step > MAX_RATING_VALUES - 1
                ) {
                    issues.push({
                        id: `question-${question.id}-rating`,
                        message: `${label || questionLabel} tem uma escala inválida.`,
                    });
                }
            }
        });
    });

    const fieldNames = allQuestions.map((question) => question.field_name).filter(Boolean);
    if (new Set(fieldNames).size !== fieldNames.length) {
        issues.push({
            id: 'question-field-name-unique',
            message: 'As perguntas precisam ter chaves internas únicas.',
        });
    }

    return issues;
};
